import type { ApiErrorBody, AuthResponse } from '@mafia/core';
import { setSiteClosed } from '@/store/site';

/**
 * Access-токен живёт только в памяти вкладки. После F5 он восстанавливается
 * через /api/auth/refresh по httpOnly-cookie.
 */
let accessToken: string | null = null;
let refreshing: Promise<AuthResponse | null> | null = null;
const listeners = new Set<(auth: AuthResponse | null) => void>();

export const getToken = () => accessToken;
export const setToken = (token: string | null) => {
  accessToken = token;
};

/** Подписка на результат обновления сессии (стор авторизации обновляет пользователя). */
export function onSessionChange(fn: (auth: AuthResponse | null) => void) {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message);
  }
  get code() {
    return this.body.error;
  }
  get fields() {
    return this.body.fields ?? {};
  }
}

async function rawRefresh(): Promise<AuthResponse | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' });
    if (res.ok) return (await res.json()) as AuthResponse;
    const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
    // Другая вкладка только что ротировала токен — новая cookie уже у браузера.
    if (body?.error === 'REFRESH_RACE') {
      await new Promise((r) => setTimeout(r, 150));
      continue;
    }
    if (body?.error === 'SITE_CLOSED') setSiteClosed(true);
    if (body?.error === 'BANNED' || body?.error === 'SITE_CLOSED') throw new ApiError(res.status, body);
    return null;
  }
  return null;
}

/** Обновить access-токен. Параллельные вызовы склеиваются в один запрос. */
export function refreshSession(): Promise<AuthResponse | null> {
  refreshing ??= rawRefresh()
    .then((auth) => {
      setToken(auth?.accessToken ?? null);
      for (const fn of listeners) fn(auth);
      return auth;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export async function api<T>(path: string, options: { method?: string; body?: unknown; retry?: boolean } = {}): Promise<T> {
  const { method = 'GET', body, retry = true } = options;
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && retry && !path.startsWith('/api/auth/')) {
    const auth = await refreshSession();
    if (auth) return api<T>(path, { ...options, retry: false });
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({ error: 'NETWORK', message: 'Сервер недоступен' }));
  if (!res.ok) {
    if ((data as ApiErrorBody).error === 'SITE_CLOSED') setSiteClosed(true);
    throw new ApiError(res.status, data as ApiErrorBody);
  }
  return data as T;
}
